import * as React from 'react';
import {
    Breakpoints,
    styled,
    ThemeProvider
} from '@volkswagen-onehub/components-core';
import throttle from 'lodash.throttle';

// Note: smaller vh on mobile due to browser UI
export const SLIDE_HEIGHT_MOBILE = '80vh';
export const SLIDE_HEIGHT_NON_MOBILE = '90vh';

export const StyledButtonWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${props => props.theme.size.static150};

    @media (max-width: ${Breakpoints.b560}px) {
        width: 100%;
        a,
        button {
            width: 100%;
        }
    }

    @media (min-width: ${Breakpoints.b1280}px) {
        flex-wrap: nowrap;
        flex-shrink: 0;
    }
`;

const StyledMediaWrapper = styled.div`
    --gradient-mobile: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 0) 45%,
        rgba(0, 0, 0, 0.5) 55%,
        rgba(0, 0, 0, 0.7) 100%
    );
    --gradient-tablet: radial-gradient(
        ellipse 175% 80% at
            ${props => (props.theme.direction === 'rtl' ? '100%' : '0%')} 100%,
        rgba(0, 0, 0, 0.7) 0%,
        rgba(0, 0, 0, 0.5) 45%,
        rgba(0, 0, 0, 0) 65%,
        rgba(0, 0, 0, 0) 100%
    );
    --gradient-non-mobile: radial-gradient(
        farthest-side at
            ${props => (props.theme.direction === 'rtl' ? 'right' : 'left')}
            bottom,
        rgba(0, 0, 0, 0.7) 0%,
        rgba(0, 0, 0, 0.5) 50%,
        rgba(0, 0, 0, 0) 70%,
        rgba(0, 0, 0, 0) 100%
    );
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: auto;

    & [class*='StyledItemOverlayWrapper'] {
        z-index: revert;
        & button,
        a {
            z-index: 3;
        }
    }

    &:after {
        content: '';
        position: absolute;
        inset: 0;
        background: var(--gradient-mobile);
        pointer-events: none;
    }

    @media (min-width: ${Breakpoints.b560}px) {
        &:after {
            background: var(--gradient-tablet);
        }
    }

    @media (min-width: ${Breakpoints.b960}px) {
        &:after {
            background: var(--gradient-non-mobile);
        }
    }
`;

const StyledSlideInner = styled.div<{
    heightOverride?: string;
}>`
    --slide-height: ${SLIDE_HEIGHT_MOBILE};
    display: flex;
    position: relative;
    inset: 0;
    flex-flow: column;
    justify-content: end;
    z-index: 2;

    ${({
        heightOverride,
        theme: {
            size: {dynamic0350, grid001, static550}
        }
    }) => `
        height: ${heightOverride ? heightOverride : 'var(--slide-height)'};
        padding: ${dynamic0350} ${grid001} ${static550};
    `};

    @media (min-width: ${Breakpoints.b560}px) {
        --slide-height: ${SLIDE_HEIGHT_NON_MOBILE};
    }
`;

const StyledContentWrapper = styled.div`
    position: relative;
    display: flex;
    flex-flow: column;
    gap: ${props => props.theme.size.static400};

    @media (min-width: ${Breakpoints.b960}px) {
        max-width: ${props => props.theme.size.grid012};
    }

    @media (min-width: ${Breakpoints.b1280}px) {
        max-width: ${props => props.theme.size.grid010};
    }

    @media (max-width: ${Breakpoints.b560 - 1}px) {
        p {
            display: none;
        }
    }
`;

type SlideContentProps = {
    mediaElement: JSX.Element;
    disclaimers?: JSX.Element;
    heightOverride?: string;
    children?: React.ReactNode;
    reportDisclaimerHeight?: (height: number) => void;
    clearDisclaimerHeights?: () => void;
};

export const SlideContent: React.FunctionComponent<SlideContentProps> =
    React.memo(
        ({
            mediaElement,
            disclaimers,
            heightOverride,
            children,
            reportDisclaimerHeight,
            clearDisclaimerHeights
        }): JSX.Element => {
            const disclaimerRef = React.useRef<HTMLDivElement | null>(null);

            const reportHeight = throttle(() => {
                if (!disclaimerRef.current) return;

                const height = disclaimerRef.current.offsetHeight;

                if (height && reportDisclaimerHeight) {
                    reportDisclaimerHeight(height);
                }
            }, 1000);

            const handleResize = React.useCallback(() => {
                if (clearDisclaimerHeights) {
                    clearDisclaimerHeights();
                }
                reportHeight();
            }, [clearDisclaimerHeights, reportHeight]);

            React.useEffect(() => {
                reportHeight();
            }, [reportHeight]);

            React.useEffect(() => {
                window.addEventListener('resize', handleResize);

                return () => {
                    window.removeEventListener('resize', handleResize);
                    reportHeight.cancel();
                };
            }, [handleResize, reportHeight]);

            return (
                <>
                    <ThemeProvider theme="inverted">
                        <StyledSlideInner heightOverride={heightOverride}>
                            <StyledMediaWrapper id="styledMediaWrapper">
                                {mediaElement}
                            </StyledMediaWrapper>
                            <StyledContentWrapper>
                                {children}
                            </StyledContentWrapper>
                        </StyledSlideInner>
                    </ThemeProvider>
                    {disclaimers && (
                        <div ref={disclaimerRef}>{disclaimers}</div>
                    )}
                </>
            );
        }
    );
